import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isSpotlightIndexingEnabled } from 'common/store/spotlightSlice';

export interface SpotlightDocument {
  id: string | number;
  name: string;
}

interface SpotlightPlugin {
  index(options: { items: { id: string; name: string }[] }): Promise<void>;
  remove(options: { ids: string[] }): Promise<void>;
  clear(options: Record<string, never>): Promise<void>;
}

let Spotlight: SpotlightPlugin | null = null;

if (Capacitor.getPlatform() === 'ios') {
  Spotlight = registerPlugin<SpotlightPlugin>('Spotlight');
}

// Keeps the on-device Spotlight index in step with the app. iOS only - the
// plugin is only ever registered above, so `Spotlight` stays null and every
// export below is a no-op everywhere else (web, Android). Every export also
// wraps its native call so a rejection can never surface to a caller as a
// thrown error, and never sends anything beyond {id, name} - never document
// contents, category, notes or any other extracted data.

// Bookkeeping for the resync below, device-local via the same Preferences
// mechanism sharedPreferences.ts already uses for other device-local state
// (e.g. authSlice.ts's getToken/userToken) - it has to survive a full app
// restart, not just live in memory for the running session, since the
// scenario this exists for is "another device deleted a document while
// THIS device's app was closed", discovered the next time this device's
// app is opened.
const LAST_SYNCED_IDS_KEY = 'spotlightLastSyncedIds';

// Defeats a specific race: clearSpotlight() is deliberately ungated (see its
// own comment below) and can be issued and complete while syncSpotlightIndex
// is still awaiting its own (potentially long-running, e.g. 2000 documents)
// Spotlight.index() call. Concretely: a dashboard sync starts indexing,
// mid-flight the user opens Account and switches the toggle off (or logs
// out, or deletes their account) - clear() lands, then the earlier index()
// call finally lands too and silently repopulates the native index with
// data that is no longer supposed to be there. The preference/account state
// now says "purged" while every document name still sits in iPhone search,
// and nothing left running will ever repair it.
//
// clearGeneration is bumped synchronously by clearSpotlight, before its own
// native call even starts, so it can never race the state it's guarding.
// syncSpotlightIndex captures the value before issuing Spotlight.index() and
// compares it after that call resolves - a mismatch means a clear happened
// during the await, so the just-written index state is stale and gets
// purged again immediately instead of trusted.
let clearGeneration = 0;

const readLastSyncedIds = async (): Promise<string[]> => {
  try {
    const { value } = await Preferences.get({ key: LAST_SYNCED_IDS_KEY });
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const writeLastSyncedIds = async (ids: string[]): Promise<void> => {
  try {
    await Preferences.set({ key: LAST_SYNCED_IDS_KEY, value: JSON.stringify(ids) });
  } catch (error) {
    // Best effort - a failed write just means the next sync recomputes its
    // diff from a stale baseline, which self-heals on the sync after that.
    console.error('Failed to persist Spotlight sync baseline:', error);
  }
};

// Genuine resync of the currently-owned document set, not additive-only.
// index() only upserts - it never removes anything - so without the prune
// step below, a document deleted from ANOTHER device (a normal case for
// anyone running Doqit on an iPhone and an iPad) would never leave THIS
// device's index. Same-device deletes are already covered by
// removeFromSpotlight below (called from the delete mutations), and this
// would otherwise only self-heal on a toggle off/on or a logout.
//
// Concretely: if the previous sync indexed ids [1, 2, 3] and the owned set
// that arrives this time is [1, 3] (2 was deleted elsewhere), this diffs
// [1, 2, 3] against [1, 3], finds 2 has dropped out, and removes exactly
// id 2 - leaving 1 and 3 untouched.
//
// Also no-ops when the user has switched indexing off - read here, inside
// the helper, so no call site can forget to check it.
//
// An empty `docs` array is treated as "this user genuinely owns zero
// documents" and wipes the whole index via the prune step below - that is
// only correct because of an invariant this function does not itself
// enforce: the caller must guarantee `docs` is either the COMPLETE current
// owned-document set or is never called at all, never a partial/failed
// fetch masquerading as empty. dashboard/index.tsx's callers hold up their
// end of that today (maybeReconcileReminders only fires once both
// categorized and uncategorized doc refs are non-null), but one real way it
// could still go wrong: if the categories endpoint ever returned every
// category with a stale `documents_count` of 0, loadCategorySignals would
// take its `nonEmpty.length === 0` branch, set `categorizedDocsRef` to `[]`,
// and this function would prune every categorized document from the index
// on the strength of a signal that was never actually a real "zero
// documents" state.
export const syncSpotlightIndex = async (docs: SpotlightDocument[]): Promise<void> => {
  if (!Spotlight || !isSpotlightIndexingEnabled()) return;

  // Captured before the (possibly slow) index() call below - see
  // clearGeneration's own comment above for the race this guards.
  const generationBeforeIndex = clearGeneration;
  const currentIds = docs.map((doc) => String(doc.id));
  const currentIdSet = new Set(currentIds);

  try {
    await Spotlight.index({
      items: docs.map((doc) => ({ id: String(doc.id), name: doc.name })),
    });
  } catch (error) {
    console.error('Failed to sync Spotlight index:', error);
    return;
  }

  // A clearSpotlight() call (toggle off, logout or account deletion) landed
  // while the index() call above was still in flight. The native index has
  // just been repopulated with document names that are no longer supposed
  // to be there. Re-purge immediately rather than trusting what we just
  // wrote, and skip the prune/baseline bookkeeping below entirely - the
  // empty baseline clearSpotlight already persisted is the correct one now,
  // not whatever this stale sync would compute.
  if (clearGeneration !== generationBeforeIndex) {
    await clearSpotlight();
    return;
  }

  const previousIds = await readLastSyncedIds();
  const droppedIds = previousIds.filter((id) => !currentIdSet.has(id));

  if (droppedIds.length === 0) {
    await writeLastSyncedIds(currentIds);
    return;
  }

  try {
    await Spotlight.remove({ ids: droppedIds });
    await writeLastSyncedIds(currentIds);
  } catch (error) {
    console.error('Failed to prune stale Spotlight entries:', error);
    // Don't advance the synced baseline - keep the dropped ids recorded so
    // the next sync retries removing them instead of forgetting they're
    // still stray in the native index.
  }
};

// De-indexes specific documents (e.g. after a confirmed delete). Also
// no-ops when indexing is switched off - if it is, the index was already
// purged by clearSpotlight() when the user turned it off (or was never
// populated since), so there is nothing to remove either way.
//
// Deliberately doesn't touch the resync baseline above: if it's briefly
// stale (still listing an id this call just removed), the worst case is
// syncSpotlightIndex's next diff re-issues a harmless remove() for an
// already-absent id (a legitimate no-op per the plugin's own contract),
// then corrects the baseline itself. Not worth a read-modify-write of
// Preferences on every single delete to avoid that one redundant call.
export const removeFromSpotlight = async (ids: (string | number)[]): Promise<void> => {
  if (!Spotlight || !isSpotlightIndexingEnabled()) return;
  try {
    await Spotlight.remove({ ids: ids.map((id) => String(id)) });
  } catch (error) {
    console.error('Failed to remove from Spotlight index:', error);
  }
};

// Purges the whole index. Deliberately NOT gated on the preference like the
// two helpers above - it has to run in two situations where that gate would
// be actively wrong:
//   1. The toggle's own "switch off" handler flips the preference to false
//      and calls this straight after. As coded today the handler calls this
//      BEFORE dispatching the preference change, so there is no live race -
//      but leaving this ungated is what guarantees a future refactor of
//      that handler (e.g. one that flips the preference first) can never
//      quietly turn the ordering into a bug that skips the one clear
//      switching off exists to guarantee.
//   2. Logout must purge the index regardless of whatever the preference
//      currently happens to be, since a signed-out user's document names
//      must never remain searchable on the device.
// This is safe unconditionally because clearing never sends or exposes
// anything - it only ever removes.
export const clearSpotlight = async (): Promise<void> => {
  if (!Spotlight) return;
  // Bumped synchronously, before the native call even starts (not after it
  // resolves) - see clearGeneration's own comment above. This guarantees
  // any syncSpotlightIndex() call already mid-flight (or one that starts
  // before this clear's own await settles) is guaranteed to see a stale
  // generation and re-purge itself once its index() call resolves, without
  // depending on the two native calls resolving in any particular order.
  clearGeneration += 1;
  try {
    await Spotlight.clear({});
    // The native index really is empty now - reset the resync baseline to
    // match, so the next syncSpotlightIndex doesn't diff against a stale
    // pre-clear list and issue a batch of redundant (harmless, but
    // pointless) remove() calls for ids that are already gone.
    await writeLastSyncedIds([]);
  } catch (error) {
    console.error('Failed to clear Spotlight index:', error);
  }
};
